import { useContext, useEffect, useRef, useState } from 'react'
import { Alert, Card, Divider, Flex, Progress, Space, Spin, Tooltip, Typography } from 'antd'
import IconAndTextButton from '../../../../IconAndTextButton'
import { faArrowRotateLeft, faCircleDown, faCircleUp } from '@fortawesome/pro-duotone-svg-icons'
import { ItemsContext } from '../../../../Items/ItemsRoot'
import { centsToDollarsDisplay, percentageDisplay, percentageDisplayWhole } from '../../../../displayFormatters'
import OverridePriceModal from './OverridePriceModal'
import { WorkareaGroup } from '../../../../../domain/workareas/group'
import { ItemClasses, PricingTypes } from '../../../../../common/constants'
import { green, grey, red, yellow } from '@ant-design/colors'
import { shouldUpdateQuotedPrice } from '../workareaEditorModel'
import { Workarea } from '../../../../../domain/job'
import { hasValue } from '../../../../../helpers/nullable'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { createViewModel } from './totalsCardModel'
import PerVisitPriceUpdater from './PerVisitPriceUpdater'
import useScreenSize from '../../../../../hooks/useScreenSize'
import { useDebouncedCallback } from '../../../../../helpers/debounce'
import { roundToPrecision } from '../../../../../helpers/math'

const { Title, Text } = Typography
const PROGRESS_TRAIL_COLOR = "#F0F0F0"

export type Props = {
  workareaId: number
  group: WorkareaGroup
  selectedWorkarea: Workarea
  cachedQuotedPrice?: number
  alreadyInvoicedAmount?: number
  onUpdateCachedQuotedPrice: (cachedQuotedPrice?: number) => void
  onCustomizationChange: (customizedFinalPrice: number | null) => void
  isSaving?: boolean
  onCalculatingChange?: (isCalculating: boolean) => void
}

const TotalsCard = ({ workareaId, group, selectedWorkarea, cachedQuotedPrice, alreadyInvoicedAmount, onCustomizationChange, onUpdateCachedQuotedPrice, onCalculatingChange, isSaving }: Props) => {
  const { workareaSummaries, jobData, isPricingPending, isPricingError } = useContext(ItemsContext)
  const [isOverridePriceModalOpen, setIsOverridePriceModalOpen] = useState(false)
  const [isLegacyCalculating, setIsCalculating] = useState(false)
  const isCalculating = isLegacyCalculating || !!isPricingPending || !!isSaving
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const laborBudget = jobData.budgets.find(x => x.class === ItemClasses.Labor)
  const desiredProfitMargingPercentage = laborBudget ? laborBudget.profit : 0
  const summary = workareaSummaries.find(x => x.workareaId === workareaId)
  const debouncedHandler = useDebouncedCallback((handler) => {
    handler(null)
  }, 2500, [onCustomizationChange])
  const screenSize = useScreenSize()

  useEffect(() => {
    if (selectedWorkarea.pricing) {
      setIsCalculating(false)
      return
    }
    if (shouldUpdateQuotedPrice(selectedWorkarea, group, summary?.estimatedPrice ?? 0)) {
      setIsCalculating(true)
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      timeoutRef.current = setTimeout(() => {
        setIsCalculating(false)
      }, 3500)
      onUpdateCachedQuotedPrice(summary?.estimatedPrice)
      debouncedHandler(onCustomizationChange)
    }
  }, [summary?.estimatedPrice, selectedWorkarea.pricing])

  useEffect(() => {
    onCalculatingChange?.(isCalculating)
  }, [isCalculating, onCalculatingChange])

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  if (isPricingError) {
    return <Card title="Totals"><Alert type="error" message="Pricing unavailable. Retry the pricing calculation to view totals." /></Card>
  }

  if (!summary) {
    return null
  }

  const total = selectedWorkarea.pricing ? summary.total : cachedQuotedPrice ?? summary.estimatedPrice
  const viewModel = createViewModel({ finalPrice: total, summaryData: summary, group, selectedWorkarea, alreadyInvoiced: alreadyInvoicedAmount })
  const isPerVisit = group.pricingType === PricingTypes.PerVisit

  return (
    <Card title="Totals">
      <Flex
        justify="space-between"
        align="center"
        wrap="wrap"
        style={{
          width: '100%',
          flexDirection: screenSize.isTabletSize ? 'column' : 'row',
          gap: 24,
        }}
      >
        <Flex
          justify="space-between"
          wrap="wrap"
          vertical={screenSize.isTabletSize}
          style={{
            width: screenSize.isTabletSize ? '100%' : '50%',
            minWidth: 260,
            flex: 1,
            marginBottom: screenSize.isTabletSize ? 16 : 0,
          }}
        >
          <Flex vertical>
            <Text type="secondary" strong>
              {isCalculating ? 'Calculating Total' : viewModel.totalData.totalText}
            </Text>
            {isCalculating ? (
              <Flex justify="center" align="center" style={{ height: 32 }}>
                <Spin size="small" />
              </Flex>
            ) : (
              <Flex gap={8} align="center">
                <Text strong style={{ fontSize: 24 }}>{centsToDollarsDisplay(viewModel.totalData.totalPrice)}</Text>
                <Text type="secondary" strong style={{ fontSize: 24 }}>{roundToPrecision(viewModel.totalData.totalHours, 2)} hrs</Text>
              </Flex>
            )}
          </Flex>

          {isPerVisit && hasValue(total) && total > 0 && hasValue(selectedWorkarea.estimatedOccurrences) && (
            <Flex vertical>
              <Text type="secondary" strong>
                Price Per Visit
              </Text>
              <Space align="center">
                <Text strong style={{ fontSize: 24 }}>
                  {centsToDollarsDisplay(viewModel.perVisitData.perVisitPrice)}
                </Text>
                <Text type="secondary" strong style={{ fontSize: 24 }}>{viewModel.perVisitData.perVisitHours} hrs</Text>
              </Space>
            </Flex>
          )}
        </Flex>

        {(group.pricingType === PricingTypes.QuotedPrice || isPerVisit) && (
          <Flex
            justify={screenSize.isTabletSize ? 'start' : 'end'}
            wrap="wrap"
            gap={12}
            style={{
              marginTop: screenSize.isTabletSize ? 8 : 16,
              width: screenSize.isTabletSize ? '100%' : '50%',
              minWidth: 220,
              flex: 1,
            }}
          >
            {!isCalculating && (
              <Tooltip title={selectedWorkarea.isApproved ? "The Total may not be edited when the work area is approved." : null} placement="top">
                <>
                  {viewModel.showResetButton ? <IconAndTextButton
                    icon={faArrowRotateLeft}
                    text={viewModel.resetButtonText}
                    size="large"
                    onClick={() => onCustomizationChange(viewModel.resetAmount)}
                    disabled={selectedWorkarea.isApproved}
                  /> : <IconAndTextButton
                    icon={faArrowRotateLeft}
                    text={viewModel.overrideButtonText}
                    size="large"
                    onClick={() => setIsOverridePriceModalOpen(true)}
                    disabled={selectedWorkarea.isApproved}
                  />}
                </>
              </Tooltip>
            )}
            {isPerVisit && !isCalculating && <PerVisitPriceUpdater
              jobId={group.jobId}
              workareaId={selectedWorkarea.id}
              currentPrice={total}
            />}
          </Flex>
        )}
      </Flex>
      {!isCalculating && viewModel.isOverridden &&
        renderDifference(
          viewModel.estimatedPrice,
          isPerVisit
            ? viewModel.perVisitData.perVisitPrice
            : total
        )}

      <Divider />

      <Flex justify="space-between" align="center">
        <Flex vertical>
          <Text type="secondary">Estimated Cost</Text>
          {isCalculating ? (
            <Flex justify="center" align="center" style={{ height: 38, marginTop: 8 }}>
              <Spin size="small" />
            </Flex>
          ) : (
            <Title level={4}>{centsToDollarsDisplay(viewModel.estimatedCost)}</Title>
          )}
        </Flex>
        <Flex vertical>
          <Text type="secondary">Breakeven</Text>
          {isCalculating ? (
            <Flex justify="center" align="center" style={{ height: 38, marginTop: 8 }}>
              <Spin size="small" />
            </Flex>
          ) : (
            <Title level={4}>{centsToDollarsDisplay(viewModel.breakeven)}</Title>
          )}
        </Flex>
        {viewModel.showRecovery && <Flex vertical>
          <Tooltip title="Estimated allowance for card processing, included in the customer total."><Text type="secondary">Recovery allowance (included)</Text></Tooltip>
          {isCalculating ? (
            <Flex justify="center" align="center" style={{ height: 38, marginTop: 8 }}>
              <Spin size="small" />
            </Flex>
          ) : (
            <Title level={4}>{centsToDollarsDisplay(viewModel.recovery)}</Title>
          )}
        </Flex>}
        <Flex vertical>
          <Text type="secondary" style={{ marginLeft: 16 }}>Net Profit</Text>
          {isCalculating ? (
            <Flex justify="center" align="center" style={{ height: 38, marginTop: 8 }}>
              <Spin size="small" />
            </Flex>
          ) : (
            <NetProfitDisplay
              netProfit={viewModel.netProfit}
              netProfitPercentage={viewModel.netProfitPercentage}
              desiredProfitMargingPercentage={desiredProfitMargingPercentage}
              total={viewModel.totalData.totalPrice}
            />
          )}
        </Flex>
      </Flex>

      <OverridePriceModal
        isOpen={isOverridePriceModalOpen}
        onClose={() => setIsOverridePriceModalOpen(false)}
        onSave={(value) => {
          setIsOverridePriceModalOpen(false)
          onCustomizationChange(value)
        }}
        currentPrice={total}
        min={viewModel.minimumCustomizableAmount}
        additionalInfo={viewModel.minimumCustomizableAmountMessage}
        isPerVisit={isPerVisit}
        recoveryIncluded={!!selectedWorkarea.pricing}
      />
    </Card>
  )
}

export default TotalsCard

const renderDifference = (estimatedPrice: number, total: number) => {
  const diff = total - estimatedPrice
  const absDiff = Math.abs(diff)
  const percentage = estimatedPrice === 0 ? 100 : Math.round((absDiff / estimatedPrice) * 100)
  const isMore = diff > 0

  const diffColor = isMore ? green.primary : red.primary
  const diffIcon = isMore ? faCircleUp : faCircleDown
  const diffTextWord = isMore ? 'more' : 'less'

  return <div>
    <Text type="secondary">
      <Text style={{ color: diffColor, fontWeight: 500 }}>
        {`${percentage}%`} <FontAwesomeIcon icon={diffIcon} color={diffColor} /> {`${diffTextWord}`}
      </Text>{' '}
      {`than the original estimated price (${centsToDollarsDisplay(estimatedPrice)})`}
    </Text>
  </div>
}

const NetProfitDisplay = ({
  netProfit,
  netProfitPercentage,
  desiredProfitMargingPercentage,
  total
}: { netProfit: number, netProfitPercentage: number, desiredProfitMargingPercentage: number, total: number }) => {
  const raw = netProfitPercentage * 100
  const isNeg = raw < 0
  const percent = isNeg ? 100 - (Math.abs(raw)) : (raw)

  const getNetProfitSectionColor = () => {
    if (raw < desiredProfitMargingPercentage && raw >= 1) {
      return yellow.primary
    }

    if (raw < 0) {
      return red.primary
    }

    return green.primary
  }
  const netProfitSectionColor = getNetProfitSectionColor()

  return <Space size="small">
    {
      raw !== 0 && <Progress
        type="circle"
        percent={percent}
        strokeWidth={15}
        size={90}
        strokeColor={isNeg ? PROGRESS_TRAIL_COLOR : netProfitSectionColor}
        trailColor={isNeg ? red.primary : PROGRESS_TRAIL_COLOR}
        strokeLinecap="butt"
        format={() => netProfitPercentage === 1 ? percentageDisplayWhole(netProfitPercentage) : percentageDisplay(raw)}
      />
    }
    <Flex vertical>
      <Title level={4} style={{ color: netProfit === 0 ? grey.primary : !total ? red.primary : netProfitSectionColor, marginBottom: 0 }}>
        {centsToDollarsDisplay(netProfit)}
      </Title>
    </Flex>
  </Space>
}